/**
 * The booking confirm's errors in the client's words (useConfirmBooking's mapping), one home for the
 * Mini App's confirm page and the studio website's review step (shared-client-screens D4, row V7).
 * Read by shape, so this module imports no app transport.
 */

/**
 * User-friendly error for UI consumption.
 * Decouples UI from ApiError transport details.
 */
export interface ConfirmBookingError {
  /** User-friendly error message */
  message: string;
  /** Error code for programmatic handling */
  code: string;
  /** Whether the user can retry the action */
  canRetry: boolean;
}

/**
 * An HTTP error from the app's client (ApiError) or the studio website's: both are Errors with a
 * numeric status, code and message.
 */
function isHttpError(error: unknown): error is { status: number; code?: string; message: string } {
  return error instanceof Error && typeof (error as { status?: unknown }).status === 'number';
}

/**
 * Map API/network errors to user-friendly ConfirmBookingError objects.
 */
export function mapConfirmBookingError(error: unknown): ConfirmBookingError {
  const defaultError: ConfirmBookingError = {
    message: 'Something went wrong. Please try again.',
    code: 'UNKNOWN_ERROR',
    canRetry: true,
  };

  // An HTTP error from the app's client (ApiError) or the studio website's
  if (isHttpError(error)) {
    const code = error.code ?? '';

    // Flow/session errors (non-retryable)
    if (code === 'FLOW_NOT_FOUND') {
      return {
        message: 'Session expired. Please start over from Telegram.',
        code: 'FLOW_NOT_FOUND',
        canRetry: false,
      };
    }

    // Session expired (401)
    if (code === 'SESSION_EXPIRED' || error.status === 401) {
      return {
        message: 'Session expired. Please start over from Telegram.',
        code: 'SESSION_EXPIRED',
        canRetry: false,
      };
    }

    if (code === 'SESSION_FLOW_MISMATCH') {
      return {
        message: 'Session mismatch. Please start over from Telegram.',
        code: 'SESSION_FLOW_MISMATCH',
        canRetry: false,
      };
    }

    if (code === 'ACCESS_DENIED') {
      return {
        message: 'Access denied. Please start over from Telegram.',
        code: 'ACCESS_DENIED',
        canRetry: false,
      };
    }

    // Generic 404 fallback
    if (error.status === 404) {
      return {
        message: 'Resource not found. Please start over from Telegram.',
        code: code || 'NOT_FOUND',
        canRetry: false,
      };
    }

    // Placeholder/slot/hold errors (non-retryable - must go back to calendar/events)
    // HOLD_EXPIRED: event booking hold expired
    // NO_PLACEHOLDER: session placeholder expired
    // NO_SLOT_SELECTED: slot context was lost
    if (code === 'HOLD_EXPIRED' || code === 'NO_PLACEHOLDER' || code === 'NO_SLOT_SELECTED') {
      return {
        message: 'Your time slot hold expired. Please select a new time.',
        code: 'PLACEHOLDER_EXPIRED',
        canRetry: false,
      };
    }

    // Service not selected
    if (code === 'SERVICE_NOT_SELECTED') {
      return {
        message: 'No service selected. Please start over.',
        code: 'SERVICE_NOT_SELECTED',
        canRetry: false,
      };
    }

    // Payment required - wrong endpoint
    if (code === 'PAYMENT_REQUIRED') {
      return {
        message: 'Payment is required for this booking. Please use the payment page.',
        code: 'PAYMENT_REQUIRED',
        canRetry: false,
      };
    }

    // Invalid token format
    if (code === 'INVALID_TOKEN_FORMAT') {
      return {
        message: 'Invalid booking link. Please use the link from Telegram.',
        code: 'INVALID_TOKEN_FORMAT',
        canRetry: false,
      };
    }

    // User not found
    if (code === 'USER_NOT_FOUND') {
      return {
        message: 'User not found. Please start over from Telegram.',
        code: 'USER_NOT_FOUND',
        canRetry: false,
      };
    }

    // Idempotency key required
    if (code === 'IDEMPOTENCY_KEY_REQUIRED') {
      return {
        message: 'Invalid request. Please try again.',
        code: 'IDEMPOTENCY_KEY_REQUIRED',
        canRetry: false,
      };
    }

    // Idempotency processing (retryable after short wait)
    if (code === 'IDEMPOTENCY_PROCESSING') {
      return {
        message: 'Request is being processed. Please wait a moment.',
        code: 'IDEMPOTENCY_PROCESSING',
        canRetry: true,
      };
    }

    // B3-3-T1: Day-lock contention (HTTP 409 LOCK_CONTENTION) — another booking for
    // the same practitioner/day is mid-confirm. Retryable after a short wait, same
    // class as IDEMPOTENCY_PROCESSING. Before B3-3 this surfaced as 400 SYSTEM_ERROR
    // (→ SERVER_ERROR, canRetry: true); the 409 reclassification must preserve
    // retryability rather than fall through to the canRetry:false generic branch.
    if (code === 'LOCK_CONTENTION') {
      return {
        message: 'The calendar is busy right now. Please try again in a moment.',
        code: 'LOCK_CONTENTION',
        canRetry: true,
      };
    }

    // Rate limiting (retryable after wait)
    if (error.status === 429 || code === 'RATE_LIMIT_EXCEEDED') {
      return {
        message: 'Too many attempts. Please wait a moment and try again.',
        code: 'RATE_LIMIT_EXCEEDED',
        canRetry: true,
      };
    }

    // Service unavailable (503)
    if (error.status === 503 || code === 'SERVICE_TEMPORARILY_UNAVAILABLE') {
      return {
        message: 'Service temporarily unavailable. Please try again.',
        code: 'SERVICE_UNAVAILABLE',
        canRetry: true,
      };
    }

    // Server/system errors (retryable)
    if (error.status >= 500 || code === 'SYSTEM_ERROR') {
      return {
        message: 'Server error. Please try again.',
        code: 'SERVER_ERROR',
        canRetry: true,
      };
    }

    // Other API errors - use message from API
    return {
      message: error.message || 'Booking failed. Please try again.',
      code: code || 'API_ERROR',
      canRetry: false,
    };
  }

  // Network errors (fetch failed entirely)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return {
      message: 'Connection error. Please check your network and try again.',
      code: 'NETWORK_ERROR',
      canRetry: true,
    };
  }

  return defaultError;
}
